import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useRouteSession } from '@web/app'
import { authClient } from '@web/auth'
import { Alert, AlertDescription } from '@layerit/ui/components/ui/alert'
import { Button } from '@layerit/ui/components/ui/button'
import { Field, FieldError, FieldLabel } from '@layerit/ui/components/ui/field'
import { Input } from '@layerit/ui/components/ui/input'
import { useOrganizations } from '@web/hooks/organizations'
import { ROUTES } from '@web/router'
import { OnboardingShell } from '@web/screens/onboarding/shell'

const nameFormSchema = z.object({
	name: z.string().trim().min(1, 'Enter the name that should appear on your signature.'),
})

type NameFormValues = z.infer<typeof nameFormSchema>

export function NameScreen() {
	const navigate = useNavigate()
	const { reload } = useRouteSession()
	const organizations = useOrganizations()
	const [failure, setFailure] = useState<string | null>(null)

	const form = useForm<NameFormValues>({
		resolver: zodResolver(nameFormSchema),
		defaultValues: { name: '' },
	})
	const name = useWatch({ control: form.control, name: 'name' })
	const ready = name.trim().length > 0 && !form.formState.isSubmitting

	if (organizations.isSuccess && organizations.data.length > 0) {
		return <Navigate to={ROUTES.chooseOrganisation} replace />
	}

	async function submit({ name: typed }: NameFormValues) {
		setFailure(null)
		const result = await authClient.updateUser({ name: typed })
		if (result.error) {
			setFailure(result.error.message ?? 'That name could not be saved.')
			return
		}
		await reload()
		await navigate(ROUTES.onboardingOrganisation, { replace: true })
	}

	return (
		<OnboardingShell
			step={0}
			title="What should we call you?"
			description="Your name appears on the signature we build for you, so it is worth getting right. You can change it later."
		>
			<form className="flex flex-col" noValidate onSubmit={(event) => void form.handleSubmit(submit)(event)}>
				<Controller
					control={form.control}
					name="name"
					render={({ field, fieldState }) => (
						<Field className="gap-2" data-invalid={fieldState.invalid || failure !== null}>
							<FieldLabel htmlFor="name">Full name</FieldLabel>
							<Input
								{...field}
								id="name"
								autoComplete="name"
								autoFocus
								required
								placeholder="Ada Lovelace"
								className="h-11"
								aria-invalid={fieldState.invalid || failure !== null}
							/>
							{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
						</Field>
					)}
				/>

				{failure && (
					<Alert variant="destructive" className="mt-5">
						<AlertDescription>{failure}</AlertDescription>
					</Alert>
				)}

				<Button type="submit" variant="primary" className="mt-7 h-11" disabled={!ready}>
					{form.formState.isSubmitting ? 'Saving…' : 'Continue'}
				</Button>
			</form>
		</OnboardingShell>
	)
}
